// Karar Masası (C04) — 4 kadran: NE / NEDEN / ETKİSİ / KARAR.
//
// KARAR + ETKİSİ kadranları FİFO çerçevesinde (CLAUDE.md §5). "Lot seç" UI'ı
// yok; "kaç unit satarsam FİFO ile hangi lotlar tüketilir, net etki ne?"
// soruları cevaplanır.

import type { SignalDetail, FifoConsumption } from "../data/mockSinyal";
import LotTablosu from "./LotTablosu";
import NavSparkline from "./NavSparkline";
import RsiEgrisi from "./RsiEgrisi";
import FreshnessIndicator from "./FreshnessIndicator";

interface Props {
  detail: SignalDetail;
  onClose: () => void;
}

const KIND_LABEL: Record<SignalDetail["kind"], string> = {
  BUY: "BUY",
  SELL: "SELL",
  PROFIT_TAKE: "PROFIT_TAKE",
};

function formatTl(n: number, signed = true): string {
  const sign = n >= 0 ? "+" : "−";
  const abs = Math.abs(n).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return signed ? `${sign}${abs} TL` : `${abs} TL`;
}

function formatUnits(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(n);
}

function buildFifoText(
  consumption: FifoConsumption[],
  lots: SignalDetail["lots"]
): string {
  // Kullanıcıya cümle olarak ver: "Lot #1 tamamı (1.000) + Lot #2'den 200 unit"
  return consumption
    .map((c) => {
      const idx = lots.findIndex((l) => l.id === c.lot_id);
      const lot = idx >= 0 ? lots[idx] : null;
      const label = idx >= 0 ? `Lot #${idx + 1}` : c.lot_id;
      if (!lot) {
        return `${label}'den ${formatUnits(c.units_consumed)} unit`;
      }
      const isFull = c.units_consumed >= lot.units - 0.001;
      if (isFull) {
        return `${label} tamamı (${formatUnits(lot.units)})`;
      }
      return `${label}'den ${formatUnits(c.units_consumed)} unit`;
    })
    .join(" + ");
}

export default function KararMasasi({ detail, onClose }: Props) {
  function handleGarantiClick() {
    // TODO: Test stage'de gerçek deeplink kalibre edilecek.
    // Placeholder URL veya yeni sekme.
    window.open(detail.garantiUrl, "_blank", "noopener,noreferrer");
  }

  const rec = detail.recommendation;
  const fifoText = buildFifoText(rec.fifo_consumption, detail.lots);

  return (
    <main
      id="screen-decision"
      className="min-h-screen flex flex-col mx-auto"
      style={{ maxWidth: 430 }}
    >
      <header className="px-4 pt-5 pb-3 flex items-start gap-3 border-b border-ink-700/60">
        <button
          type="button"
          aria-label="Kapat"
          onClick={onClose}
          className="text-ink-300 hover:text-ink-100 p-2 -ml-2 shrink-0"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="grow min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-ink-100 fon-dili">
              {detail.fund}
            </h2>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-warn-500/15 text-warn-400 font-medium">
              {KIND_LABEL[detail.kind]}
            </span>
          </div>
          <p className="text-[12px] text-ink-300 mt-1">
            <span className="text-ink-200 font-medium">
              skor {detail.score}
            </span>
            <span className="mx-1.5 text-ink-500">·</span>
            <span className="fon-dili">
              RSI(14) {detail.rsi14} · RSI(5) {detail.rsi5}
            </span>
          </p>
          <p className="hedef-dili text-ink-100 text-sm mt-1.5 leading-snug">
            {detail.headline}
          </p>
          <div className="mt-2">
            <FreshnessIndicator
              lastPriceDate={detail.triggered_at}
              indexPeriod="2026-04"
              signalRefreshedAt={detail.triggered_at}
              mock
              compact
            />
          </div>
        </div>

        <button
          type="button"
          aria-label="Karar masasını kapat"
          onClick={onClose}
          className="text-ink-300 hover:text-loss-400 p-2 -mr-2 shrink-0"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </header>

      <section
        className="grid grid-cols-2 gap-2 p-2"
        aria-label="Karar masası kadranları"
      >
        {/* Kadran 1: NE oldu */}
        <article className="rounded-xl bg-ink-800 border border-ink-700 p-3 card-shadow">
          <header className="flex items-center justify-between mb-1">
            <h3 className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
              NE oldu
            </h3>
            <span className="fon-dili text-[10px] text-ink-500">
              {detail.navWindowDays}g
            </span>
          </header>
          <p className="hedef-dili text-ink-100 text-sm leading-snug">
            {detail.navSummary}{" "}
            <span
              className={
                detail.navChangePct >= 0 ? "text-gain-400" : "text-loss-400"
              }
            >
              {detail.navChangePct >= 0 ? "+" : "−"}%
              {Math.abs(detail.navChangePct).toFixed(1).replace(".", ",")}
            </span>
          </p>
          <NavSparkline
            path={detail.navPath}
            fillPath={detail.navFillPath}
            markers={detail.navMarkers}
            ariaLabel={`${detail.navWindowDays} günlük NAV grafiği`}
          />
          <p className="text-[10px] text-ink-400 mt-1">
            {detail.navMarkers.length} lot giriş noktası işaretli (•)
          </p>
        </article>

        {/* Kadran 2: NEDEN */}
        <article className="rounded-xl bg-ink-800 border border-ink-700 p-3 card-shadow">
          <header className="flex items-center justify-between mb-1">
            <h3 className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
              NEDEN
            </h3>
            <span className="fon-dili text-[10px] text-ink-500">RSI(14)</span>
          </header>
          <p className="hedef-dili text-ink-100 text-sm leading-snug">
            RSI{" "}
            <span className="fon-dili text-warn-400 font-semibold not-italic">
              {detail.rsi14}
            </span>{" "}
            — {detail.rsiSummary.split("—")[1]?.trim() ?? "aşırı alım"}
          </p>
          <RsiEgrisi
            path={detail.rsiPath}
            valueX={detail.rsiValueX}
            valueY={detail.rsiValueY}
          />
          <p className="text-[10px] text-ink-400 mt-1">{detail.rsiNote}</p>
        </article>

        {/* Kadran 3: ETKİSİ — FİFO sıralı lot tablosu */}
        <article className="rounded-xl bg-ink-800 border border-ink-700 p-3 card-shadow col-span-2">
          <header className="flex items-center justify-between mb-2">
            <h3 className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
              ETKİSİ · {detail.lots.length} lot · FİFO
            </h3>
            <span className="fon-dili text-[10px] text-ink-500">
              eski → yeni
            </span>
          </header>
          <LotTablosu lots={detail.lots} />
        </article>

        {/* Kadran 4: KARAR — FİFO öneri */}
        <article className="rounded-xl bg-accent-500/10 border border-accent-500/50 p-3 card-shadow col-span-2">
          <header className="flex items-center gap-2 mb-1">
            <span
              className="w-2 h-2 rounded-full bg-accent-400"
              aria-hidden="true"
            />
            <h3 className="text-[10px] uppercase tracking-wider text-accent-300 font-semibold">
              KARAR
            </h3>
            <span className="ml-auto text-[11px] text-ink-300 fon-dili">
              öneri
            </span>
          </header>
          <p className="hedef-dili text-ink-50 text-base leading-snug">
            <span className="not-italic fon-dili text-accent-300 font-semibold">
              {formatUnits(rec.units_to_sell)} unit sat.
            </span>{" "}
            FİFO ile {fifoText} tüketilir.
          </p>
          <p className="text-[12px] text-ink-300 mt-1.5 leading-snug">
            Vergi-sonrası net{" "}
            <span className="fon-dili text-gain-400 font-semibold">
              {formatTl(rec.net_tl)}
            </span>{" "}
            ·{" "}
            <span className="fon-dili">
              stopaj{" "}
              {rec.tax_tl.toLocaleString("tr-TR", {
                maximumFractionDigits: 0,
              })}{" "}
              TL
            </span>{" "}
            ·{" "}
            <span className="hedef-dili text-accent-300 not-italic">
              hedef etkisi {rec.days_impact >= 0 ? "+" : "−"}
              {Math.abs(rec.days_impact).toFixed(1).replace(".", ",")} gün
            </span>
            .
          </p>
          <button
            type="button"
            onClick={handleGarantiClick}
            className="mt-3 w-full rounded-lg bg-accent-500 hover:bg-accent-400 text-ink-900 font-medium text-sm py-2.5 flex items-center justify-center gap-2 transition"
          >
            Garanti'ye geç
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
          <p className="text-[10px] text-ink-400 mt-2 text-center">
            manuel emir · FİFO sırasıyla işlenir · AiFon işlem yapmaz
          </p>
        </article>
      </section>

      <footer className="mt-auto px-4 pb-6 pt-4 text-center text-[11px] text-ink-400">
        AiFon · 2026
      </footer>
    </main>
  );
}
